import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Button, TextLink } from "@vds/buttons";
import { Col, Grid } from "@vds/grids";
import { Input } from "@vds/inputs";
import { Line } from "@vds/lines";
import { DropdownSelect } from "@vds/selects";
import { TileContainer } from "@vds/tiles";
import { Text } from '@vds/core';
import { ColorTokens } from "@vds-tokens/color";
import { Notification } from "@vds/notifications";
import AsyncSelectInput from "common/components/AsyncSelectInput";
import { ProtectionPlans, SpinnerComponent } from "common/components";
import {
  MarginSpacerL,
  MarginSpacerM,
  MarginSpacerS,
} from "common/components/styleTag";
import useToggle from "hooks/useToggle";
import apiClient from "utils/apiClient";
import { VALIDATE_DEVICE_ID, VALIDATE_SIM_ID } from "containers/APIurls";
import {
  getCarrierDetailsByDevice,
  validateZipAndRetrieveFeatureOnBYOD,
  retrieveFeatureOnBYOD,
  setImeiWithPsimEsimCapable,
} from "pages/Mobility/ducks/MobilitySlice";
import {
  ButtonContainer,
  RowNoPad,
  AsyncWrapper,
  CustomRadioBoxGroup,
  SimRadioBoxGroup,
} from "./styles";
import EsimConfirmationModal from "../../eSimConfirmation";
import DeviceTileSection from "./DeviceTileSection";
import SelectedDevice from "./SelectedDevice";
import { current } from "@reduxjs/toolkit";
import { PDPAccordionSection } from "common/components/ProductDetails/components";
import { setReinitiateFlow } from "app/ducks/GlobalSlice";
import { getProductType } from "utils/productTypeUtil";
import * as SiteAnalytics from "../../../../sitecatalyst";

const DeviceTile = ({
  sno,
  device,
  isFromLearn,
  byodList,
  showRemove = false,
  pathUrl,
  setLoading = () => { },
  handleByodRemove = () => { },
  handleDeviceUpdate = () => { },
  handleDeviceUpdateAgent = () => { },
  invalidDeviceHandler = () => { },
  handleDeviceDetailsModal = () => { },
  isMobile,
  deviceList,
  isPlansFirst,
  selectedDeviceId,
  handleDeviceId,
  currentDevice,
  showByod,
  showByodPage,
  setShowByod = () => { },
  setShowByodPage = () => { },
  setByodDeviceList = () => { },
  showLearnEditPage,
  setAddMoreDevice = () => { },
  handleMultiDeviceUpdate = () => { },
  isEditPackageFlow,
  setIsConnectedDevices = () => { },
  isConnectedDevices = false,
  setShowLegalDetails = () => {},
  isByodAgentLinkPage,
  byodAgentList
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["byod", "app"]);
  const [showButton, setShowButton] = useState(true);
  const [inputValue, setInputValue] = useState({
    imei: device?.imei,
    iccid: device?.iccid,
  });
  const pageData = useSelector((state) => state.MobilityPage);
  const globalData = useSelector((state) => state.GlobalData);
  const [showDeviceId, setShowDeviceID] = useState(false);
  const [isMakeAndModal, setIsMakeAndModal] = useState(
    false || device?.isEditable
  );
 
  const isAgentLinkPage = useSelector(
    (state) => state?.GlobalData?.fromAgentLinkDealsPage
  );
  const metaInformation = useSelector(
    (state) => state.GlobalData?.metaInformation
  );
  const sessionCartData = useSelector(
    (state) => state.GlobalData?.sessionCartData?.apiResponse?.prospectFlowData
  );
  const [showForm, setShowForm] = useState(
    (!device.completed && !device?.isDQ) || device?.isEditable
  );
  const [isVZNumber, setIsVZNumber] = useState(device?.isVZNumber ?? "");
  const [invalidDeviceError, setInvalidDeviceError] = useState(false);
  const [imeiCodeValid, setimeiCodeValid] = useState(true);
  const [showOptions, toggleOptions] = useToggle(
    !!device.deviceInfo && !!device.imei
  );
  const [deviceInfo, setDeviceInfo] = useState(device?.deviceInfo);
  const [showVZOptions, setShowVZOptions] = useState(false);
  const [eSimconfirmationModal, setConfirmationModal] = useState(false);
  const [selected, setSelected] = useState(device.selectedOpt);
  const [isNewSim, setIsNewSim] = useState(device.selectedOpt === "new");
  const [isOldSim, setIsOldSim] = useState(device.selectedOpt === "old");
  const [isESIM, setIsESIM] = useState(device.selectedOpt === "esim");
  const [psimCapableN, setPsimCapableN] = useState(false);
  const [isCarrier, setIsCarrier] = useState(false);
  const [isEsimIndicator, setIsEsimIndicator] = useState(false);
  const [isOldSimVerified, setIsOldSimVerified] = useState(
    device?.isOldSimVerified || false
  );
  const [showCancel, setShowCancel] = useState(false || device?.isEditable);
  const [currentDeviceSelected, setCurrentDeviceSelected] = useState(false);

  const [deviceName, setDeviceName] = useState(
    device?.deviceName || "smartphone"
  );
  const [byodDevice, setByodDevice] = useState({});
  const [simTypeSelected, setSimTypeSelected] = useState(false);
  const [carrierSelected, setCarrierSelected] = useState(false);
  const [deviceSelected, setDeviceSelected] = useState(false);
  const [showValidateDeviceId, setShowValidateDeviceId] = useState(false);
  const [imeiErrorText, setImeiErrorText] = useState("");
  const [maxLengthValue, setMaxLengthValue] = useState(0);
  const [deviceErrorText, setDeviceErrorText] = useState(
    `${t("byod.errorMessages.invalidDevice", { ns: "byod" })}`
  );
  const [selectedDevice, setSelectedDevice] = useState(
    device?.selectedDevice ?? null
  );
  const [carriersOpts, setCarriersOpts] = useState(
    device?.selectedDevice?.carrier ?? []
  );
  const [selectedCarrier, setSelectedCarrier] = useState(
    device?.selectedCarrier ?? null
  );
  const [validateIdLoading, setValidateIdLoading] = useState(false);

  const simOptions =
    t("byod.byod-collection.simOptions", { ns: "byod", returnObjects: true }) ??
    [];
  const imeiAllowedChars = "0-9";
  const handleByodRemoveClick = () => handleByodRemove(device.id);
  const [enableChecker, setEnableChecker] = useState(false);

  const isPDPProtectionEnabled = metaInformation?.showProtectionFeaturesOnPDP &&
    !(
      deviceInfo?.category?.toLowerCase()?.includes("laptop")
    );
  const [showProtection, setShowProtection] = useState(false);
  const [showProtectionAcc, setShowProtectionAcc] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(
    device?.selectedFeature ?? null
  );
  const [isProtectionLoading, setIsProtectionLoading] = useState(false);
  const [isFeatureError, setFeatureError] = useState(false);
  const [isTMPMDonInit, setTMPMDonInit] = useState(null);
  const [isFeaturesRetrieved, setRetriveFeatures] = useState(false)

  const isTMPMDPlaninCart =
    sessionCartData?.cart?.accountLevelFeatures &&
    Object.values(sessionCartData?.cart?.accountLevelFeatures)?.filter(a => a?.featureDesc?.includes("Total Mobile Protection Multi-Device")).length >= 1
  const isTMPMDProtectionPlanSelected = !!isPDPProtectionEnabled && isTMPMDPlaninCart;
  const isTMPMDProtectionPlanSelectedFromByod = deviceList && Object.values(deviceList)?.filter(d => d?.selectedFeature?.displayName?.includes("Total Mobile Protection Multi-Device")).length >= 1
  const offerTextPortInObj = t("app.portinOffersTextContainer.byod", { ns: "app", returnObjects: "true" }) ;
  const showOfferText = t("app.portinOffersTextContainer.enableOfferText", { ns: "app", returnObjects: "true" }) ;
  const [offerTextPortIn , setOfferTextPortIn] = useState("");
  const payload = {
    shoppingPath: "PROSPECT",
    approach: "",
    flow: "prospect",
  };

  const optionsList = useMemo(() => {
    const opts =
      t("byod.deviceTypeOptions", { ns: "byod", returnObjects: true }) ?? [];
    return isPlansFirst
      ? opts.filter((i) => i?.value?.toLowerCase() === "smartphone")
      : isAgentLinkPage
        ? opts.filter(
          (i) =>
            i?.value?.toLowerCase() === "laptop" ||
            i?.value?.toLowerCase() === "tablet"
        )
        : opts;
  }, [t, isPlansFirst]);

  const onDeviceTypeChange = (val, fromLearn = false) => {
    if (
      val === "tablet" ||
      val === "hotspot" ||
      val === "other" ||
      val === "laptop"
    ) {
     
      setDeviceName(val);
      showDeviceIdOption(val);
      setIsEsimIndicator(false);
    } else {
     
      showMakeAndModelOption(val);
      setDeviceName(val);
      setIsEsimIndicator(false);
    }
    if (val === "smartphone") {
      setCurrentDeviceSelected(true);
    } else {
      setCurrentDeviceSelected(false);
    }
    
    if (
      fromLearn === true &&
      val === "smartphone" &&
      byodDevice?.selectedCarrier
    ) {
      const selectedOption = {
        carrier: ["Verizon", "T-Mobile", "AT&T", "Other"], //byodDevice.selectedCarrier
        imageName: byodDevice.imageSet,
        make: byodDevice.make,
        modelName: byodDevice.modal,
        sku: byodDevice.deviceSku,
        technology: byodDevice.technologyType,
      };
      handleOnDeviceSelect(selectedOption, true);
    } else if (fromLearn === true && byodDevice?.imei) {
      inputClickHandler(true);
    }
  };
  const onDeviceTypeChangeLearn = (val) => {
    if (
      val === "tablet" ||
      val === "hotspot" ||
      val === "other" ||
      val === "laptop"
    ) {
      setDeviceName(val);
      showDeviceIdOption(val);
      setIsEsimIndicator(false);
    } else {
      showMakeAndModelOption(val);
      setDeviceName(val);
      setIsEsimIndicator(false);
    }
    if (val === "smartphone") {
      setCurrentDeviceSelected(true);
    } else {
      setCurrentDeviceSelected(false);
    }

    if (val === "smartphone" && byodDevice?.selectedCarrier) {
      const selectedOption = {
        carrier: ["Verizon", "T-Mobile", "AT&T", "Other"], //byodDevice.selectedCarrier
        imageName: byodDevice.imageSet,
        make: byodDevice.make,
        modelName: byodDevice.modal,
        sku: byodDevice.deviceSku,
        technology: byodDevice.technologyType,
      };
      handleOnDeviceSelect(selectedOption, true);
    } else if (byodDevice?.imei) {
     
      handleFormInputChange(byodDevice.imei, "imei", true);
    }
  };
  const PATTERN_IMEI = new RegExp(`^(?=[${imeiAllowedChars}]*$)(?:.{15})$`);
  const imeiWithPsimEsimCapable = pageData?.imeiWithPsimEsimCapable ?? [];

  useEffect(() => {
    setShowLegalDetails(showProtection && pageData?.zipCode || selectedFeature)
  }, [showProtection, pageData?.zipCode]);

  useEffect(() => {
    const { imei } = inputValue;
    setEnableChecker(PATTERN_IMEI.test(imei));
  }, [inputValue.imei]);

  useEffect(() => {
    if (device?.isEditable && device?.displayType?.length > 1) {
      handleByodEditClick();
    }
    if (
      (!device.completed && !device?.isDQ) ||
      device?.isEditable ||
      (device?.isDQ && !device?.completed)
    ) {
      !showForm && setShowForm(true);
    }
  }, [device]);

  useEffect(() => {
    if (selectedCarrier === "Other") {
      setIsMakeAndModal(true);
      setIsCarrier(true);
    } else {
      setIsCarrier(false);
    }
    if (deviceName) {
      setDeviceName(deviceName);
    }
  }, [deviceName, selectedCarrier]);

  useEffect(() => {
    let selectedDevice = "";
    if (
      device !== null &&
      device &&
      Object.keys(device || {}).length > 0 &&
      !device?.completed &&
      !String(device.deviceName).toLowerCase().includes("smartphone")
    ) {
      selectedDevice = pageData.deviceListBy[0].deviceName;
      setShowForm(true);
      setShowDeviceID(true);
    }
    if (!selectedDevice && showForm) {
      let isOptionIncluded = false;
      if (!device?.isDQ) {
        optionsList.forEach((item) => {
          if (pathUrl?.includes(item?.value.toLowerCase())) {
            onDeviceTypeChange(item?.value.toLowerCase());
            isOptionIncluded = true;
          } else if (pathUrl?.includes("all")) {
            onDeviceTypeChange("smartphone");
            isOptionIncluded = true;
          }
        });
      }
      if (!isOptionIncluded) {
        onDeviceTypeChange("other");
      }
    }
  }, [pathUrl]);

  useEffect(() => {
    if (
      device?.isDQ &&
      device?.message &&
      !device?.completed &&
      !device?.selectedDevice &&
      !device?.selectedOpt
    ) {
      let deviceName = device?.message?.toLowerCase();
      if (deviceName?.includes("netbook") || deviceName?.includes("tab")) {
        deviceName = "tablet";
      } else if (deviceName?.includes("internet")) {
        deviceName = "hotspot";
      } else if (deviceName?.includes("smartphone")) {
        deviceName = "smartphone";
      } else if (deviceName?.includes("laptop")) {
        deviceName = "laptop";
      } else {
        deviceName = "other";
      }
      onDeviceTypeChange(deviceName);
    }
    if (
      deviceInfo &&
      deviceInfo?.psimCapable === "Y" &&
      deviceInfo?.esimCapable === "Y" &&
      inputValue?.imei === deviceInfo?.imei2
    ) {
      setPsimCapableN(true);
    }
    if (isEditPackageFlow) {
      setIsProtectionLoading(true);
      dispatch(retrieveFeatureOnBYOD({
        shoppingPath: "PROSPECT",
        flow: "prospect",
        zipCode: pageData?.zipCode,
        getFeatureRequest: getFeatureRequestPayload()
      }));
    }
  }, []);

  useEffect(() => {
    if (
      deviceInfo &&
      deviceInfo?.esimCapable === "Y" &&
      (deviceInfo?.psimCapable === "N" || deviceInfo?.psimCapable === undefined)
    ) {
      setSelected("esim");
      setIsEsimIndicator(false);
    }
    if (
      deviceInfo &&
      deviceInfo?.psimCapable === "Y" &&
      (deviceInfo?.esimCapable === "N" || deviceInfo?.esimCapable === undefined)
    ) {
      setSelected("new");
      setIsNewSim(true);
      setIsEsimIndicator(false);
    }
    if (
      deviceInfo &&
      deviceInfo?.psimCapable === "Y" &&
      deviceInfo?.esimCapable === "Y"
    ) {
      setIsEsimIndicator(true);
    }
    if (
      deviceInfo &&
      deviceInfo?.psimCapable === "Y" &&
      deviceInfo?.esimCapable === "Y" &&
      inputValue?.imei !== deviceInfo?.imei2
    ) {
      setPsimCapableN(false);
    }
    const deviceCat = deviceInfo?.category?.toLowerCase();
    if (deviceCat?.includes("laptop")) {
      setSelectedFeature(null);
    }
  }, [deviceInfo]);

  useEffect(() => {
    if (selected) {
      handleOnSelect();
      toggleOptions(true);
      if (selected === "new") {
        setInputValue({ ...inputValue, iccid: "" });
        setIsOldSimVerified(false);
        setIsCarrier(false);
      }

      if (
        (selected === "esim" &&
          pageData?.carrierDetailsByDevice?.esimOnlySupportedIndicator !==
          "Y") ||
        selected === "new" ||
        selected === "old"
      ) {
        setShowVZOptions(true);
      }
    }
  }, [selected]);

  const generateOfferText = (productType) => {
    let offerText= offerTextPortInObj?.[productType]
    setOfferTextPortIn(offerText);
    productType == "desk-conference-phones" ? setIsConnectedDevices(true) : setIsConnectedDevices(false) ;
  }
  
  useEffect(() => {
    if (
      pageData?.carrierDetailsByDevice &&
      device?.deviceName === "smartphone"
    ) {
      if (
        pageData?.carrierDetailsByDevice?.esimOnlySupportedIndicator === "Y" &&
        selectedDevice
      ) {
        const data = {
          selectedOpt: "esim",
          isVZNumber: "",
          selectedCarrier: null,
          deviceSku: pageData?.carrierDetailsByDevice?.deviceSku,
          simSku: pageData?.carrierDetailsByDevice?.selectedSimSku,
          category: pageData?.carrierDetailsByDevice?.category,
        };
        if (showOptions) {
          toggleOptions(false);
        }
        setSelected("");
        setIsESIM(false);
        handleDeviceUpdate(data, device.id);
        setIsCarrier(true);
        setIsMakeAndModal(false);
        setShowDeviceID(true);
        setShowVZOptions(false);
        setSelectedCarrier("");
      } else {
        if (selectedDeviceId === device?.id) {
          const data = {
            selectedOpt: null,
            isVZNumber: "",
            selectedCarrier: device?.selectedCarrier || null,
            deviceSku: pageData?.carrierDetailsByDevice?.deviceSku,
            simSku: pageData?.carrierDetailsByDevice?.selectedSimSku,
            category: pageData?.carrierDetailsByDevice?.category,
          };
          setIsEsimIndicator(false);
          setIsESIM(false);
          setIsMakeAndModal(true);
          handleDeviceUpdate(data, device.id);
          setSelected("");
        }
      }
    }
    if(pageData?.carrierDetailsByDevice){
      let productType = getProductType(pageData?.carrierDetailsByDevice?.category ?? "");
      generateOfferText(productType ?? "");
    }
  }, [pageData.carrierDetailsByDevice]);

  useEffect(() => {
    if (byodList && byodList.length > 0 && !showByod) {
      const byodData = byodList?.[0];
      setByodDevice(byodData);
    }
  }, [byodList]);

  useEffect(() => {
    if (byodDevice && byodDevice?.message && showLearnEditPage) {
      let deviceName = byodDevice?.message?.toLowerCase();
      if (deviceName?.includes("netbook") || deviceName?.includes("tab")) {
        deviceName = "tablet";
      } else if (deviceName?.includes("internet")) {
        deviceName = "hotspot";
      } else if (deviceName?.includes("smartphone")) {
        deviceName = "smartphone";
      } else if (deviceName?.includes("laptop")) {
        deviceName = "laptop";
      } else {
        deviceName = "other";
      }
      onDeviceTypeChangeLearn(deviceName);
      setShowByod(true);
    }
  }, [byodDevice]);

  useEffect(()=>{
    if(byodAgentList && byodAgentList?.length>0 && globalData?.isAgentlink && sessionCartData?.fromAgentLinkPage && !showByodPage){
      byodAgentList?.forEach((item)=>{
        let deviceName = item?.message?.toLowerCase();
      if (deviceName?.includes("netbook") || deviceName?.includes("tab")) {
        deviceName = "tablet";
      } else if (deviceName?.includes("internet")) {
        deviceName = "hotspot";
      } else if (deviceName?.includes("smartphone")) {
        deviceName = "smartphone";
      } else if (deviceName?.includes("laptop")) {
        deviceName = "laptop";
      } else {
        deviceName = "other";
      }
      setDeviceName(deviceName);
      showDeviceIdOption(deviceName);
      setIsEsimIndicator(false);
      setShowForm(true)
      setShowByodPage(true);
      setSelectedFeature(item?.feature);
      isVzSelected(item.isVZNumber, false,item?.feature);
      });

    }

  },[byodAgentList])

  useEffect(() => {
    if (showByod === true) {
      updateDeviceList({});
    }
  }, [showByod]);
  useEffect(() => {
    if (showByodPage === true) {
      updateDeviceList(byodAgentList);
    }
  }, [showByodPage]);

  useEffect(() => {
    if (simTypeSelected === true) {
      updateDeviceList({});
      isVzSelected(byodDevice.isVZNumber, true);
      setSimTypeSelected(false);
    }
  }, [simTypeSelected]);

  useEffect(() => {
    if (deviceSelected === true) {
      handleCarrierSelect(byodDevice.selectedCarrier, true);
      setDeviceSelected(false);
    }
  }, [deviceSelected]);

  useEffect(() => {
    if (carrierSelected === true) {
      updateDeviceList({});
      isVzSelected(byodDevice.isVZNumber, true);
      setCarrierSelected(false);
    }
  }, [carrierSelected]);

  useEffect(() => {
    if (showValidateDeviceId === true) {
      handleOnChangeFromLearn();
      setShowValidateDeviceId(false);
    }
  }, [showValidateDeviceId]);

  useEffect(() => { 
    // remove protection loading indicator
    setIsProtectionLoading(false);
     // check whether TMP-MultiDevice is available yet
    if (isTMPMDonInit === null) {
      setTMPMDonInit(isMultiDeviceProtectPresent(pageData?.PDPFeatureList));
    }
     // preselect multi-device plan, if already added
    if (sessionCartData?.cart?.packages && pageData?.PDPFeatureList?.length) {
      let multiDevInCart;
      for (const pack of Object.values(sessionCartData?.cart?.packages || {})) {
        if (pack?.features && Object.values(pack?.features ?? {})?.length > 0) {
          for (const [id, feat] of Object.entries(pack?.features || {})) {
            if (
              feat?.featureDesc?.includes(
                "Total Mobile Protection Multi-Device for Business"
              )
            ) {
              multiDevInCart = id;
              break;
            }
          }
        }
      }
      if (!!multiDevInCart) {
        const mdFeature = pageData?.PDPFeatureList?.find(
          (i) => i?.billingCode === multiDevInCart
        );
        setSelectedFeature({
          displayName: mdFeature?.name,
          featureCode: mdFeature?.billingCode,
          featureType: "SFO",
          featureLevel: "A",
          quantity: "1",
        });
      }
    }
    // check for multi-device feature in previous BYOD added
    if (deviceList?.length) {
      let multiDevInBYODList;
      for (const dev of deviceList) {
        if (dev?.selectedFeature?.displayName?.includes("Total Mobile Protection Multi-Device for Business")) {
          multiDevInBYODList = dev?.selectedFeature?.featureCode;
        }
      }
      if (!!multiDevInBYODList) {
        const mdFeature = pageData?.PDPFeatureList?.find(
          (i) => i?.billingCode === multiDevInBYODList
        );
        setSelectedFeature({
          displayName: mdFeature?.name,
          featureCode: mdFeature?.billingCode,
          featureType: "SFO",
          featureLevel: "A",
          quantity: "1",
        });
      }
    }
  }, [pageData?.PDPFeatureList]);

  const validateInput = (value, inputName) => {
    if (inputName === "imei") {
      const input = deviceList.filter(
        (d) => d.imei === value && d.id !== device.id
      );
      if (value.length < 1) {
        setimeiCodeValid(false);
        setInvalidDeviceError(false);
        setImeiErrorText(t("byod.errorMessages.requiredField"), {
          ns: "byod",
        });
        return;
      }
      if (!PATTERN_IMEI.test(value)) {
        setimeiCodeValid(false);
        setInvalidDeviceError(false);
        setImeiErrorText(t("byod.errorMessages.deviceidLength"), {
          ns: "byod",
        });
        return;
      }
      if (Array.isArray(input) && input.length > 0) {
        setimeiCodeValid(false);
        setInvalidDeviceError(false);
        setImeiErrorText(t("byod.errorMessages.existingId"), {
          ns: "byod",
        });
        return;
      }
      setimeiCodeValid(true);
      setInvalidDeviceError(false);
    }
  };

  const handleFormInputChange = (value, inputName, fromLearn = false) => {
    if (inputName === "imei") {
      let val = value.replace(/[^0-9]/g, "");
      val = value.replace(/\s/g, '')
      if (val?.length >= 15) {
        setMaxLengthValue(15)
      }
      validateInput(val.trim(), inputName);
      setInputValue({ ...inputValue, [inputName]: val.trim() });
      if (fromLearn === true) {
        checkDeviceID(true);
      }
    } else {
      validateInput(value.trim(), inputName);
      setInputValue({ ...inputValue, [inputName]: value.trim() });
    }
  };

  const updateDeviceList = ({
    isVZNum = null,
    fromLearn = false,
    feature = null,
  }) => {
    const data = {
      selectedCarrier: selectedCarrier,
      isVZNumber: (isVZNum ?? isVZNumber ?? device?.isVZNumber) || "",
      selectedDevice: selectedDevice,
      completed: true,
      displayType: selectedCarrier ? "ByMakeAndMod" : "ByImei",
      isEditable: false,
      selectedOpt: selected,
      imei: inputValue.imei,
      iccid: inputValue.iccid,
      deviceInfo: deviceInfo,
      isOldSimVerified: isOldSimVerified,
      completed: true,
      fromLearn: fromLearn,
      packageId: fromLearn ? byodDevice?.packageId : (device?.packageId ? device?.packageId : ""),
      selectedFeature: feature,
    };
    if (device?.packageId) {
      data.updated = true;
    }
    if(globalData?.isAgentlink && sessionCartData?.fromAgentLinkPage){
      handleDeviceUpdateAgent(data, device.id)
    }else{
      handleDeviceUpdate(data, device.id);
    }
  };

  const checkDeviceID = async (fromLearn = false) => {
    setValidateIdLoading(true);
    try {
      let payload = {
        approach: "",
        flow: "prospect",
        deviceId: fromLearn === true ? byodDevice?.imei : inputValue.imei,
        shoppingPath: "PROSPECT",
      };
      if (isPDPProtectionEnabled && pageData?.zipCode) {
        payload.zipCode = pageData.zipCode;
        payload.getFeatureRequest = getFeatureRequestPayload({
          deviceId: fromLearn === true ? byodDevice?.imei : inputValue.imei,
        });
      }
      let url = VALIDATE_DEVICE_ID;
      if (fromLearn === true && byodDevice.iccid) {
        payload = { ...payload, simNumber: byodDevice.iccid };
        url = VALIDATE_SIM_ID;
      } else if (inputValue.iccid) {
        payload = { ...payload, simNumber: inputValue.iccid };
        url = VALIDATE_SIM_ID;
      }
      setLoading();
      const { data } = await apiClient.post(url, payload);
      const {
        serviceStatus: { message, success },
        deviceInfo: info,
      } = data;
      if (!success) {
        setInvalidDeviceError(true);
        setDeviceErrorText(
          t("byod.errorMessages.invalidDevice", { ns: "byod" })
        );
        SiteAnalytics.errorEncounter({"data":data,"id":data?.serviceStatus?.statusCode,"title":data?.serviceStatus?.statusMessage,"subtitle":"Device ID validation"});
      } else {
        if(data && Object.keys(data || {} )?.length && data?.deviceInfo?.esimCapable == "Y" && data?.deviceInfo?.psimCapable == "Y"){
          let updatedArrImei = [...imeiWithPsimEsimCapable, {pSim : data?.deviceInfo?.imei1, eSim : data?.deviceInfo?.imei2 }]
          dispatch(setImeiWithPsimEsimCapable(updatedArrImei))
        }
        setInvalidDeviceError(false);
        featurePreSelect();
      }
      if (
        isPlansFirst &&
        !info?.category?.toLowerCase()?.includes("smartphone")
      ) {
        // plans first is only for smartphones
        setInvalidDeviceError(true);
        setDeviceErrorText(
          t("app.notifications.byod.plans-first-non-compatible", {
            ns: "app",
          })
        );
      } else {
        if (fromLearn === true) {
          if (message === "SUCCESS" && !byodDevice.iccid) {
            toggleOptions();
            setShowButton(false);
            setDeviceInfo(info);
          }
          if (message === "SUCCESS" && (byodDevice.iccid || byodDevice.imei)) {
            setIsOldSimVerified(true);
            setimeiCodeValid(false);
          } else {
            setimeiCodeValid(true);
          }
        } else {
          if (message === "SUCCESS" && !inputValue.iccid) {
            toggleOptions();
            setShowButton(false);
            setDeviceInfo(info);
          }
          if (message === "SUCCESS" && (inputValue.iccid || inputValue.imei)) {
            setIsOldSimVerified(true);
            setimeiCodeValid(false);
          } else {
            setimeiCodeValid(true);
          }
        }
      }
      setValidateIdLoading(false);
      if (fromLearn === true) {
        setShowValidateDeviceId(true);
      }

      if(info){
        let productType = getProductType(info?.category ?? "");
        generateOfferText(productType ?? "");
      }
    } catch (err) {
      console.log("IEMI_CHECK_ERR", err);
    } finally {
      setLoading();
    }
  };

  const handleCarrierSelect = (val, fromLearn = false) => {
    if (val !== "Select") {
      if (val === "Other") {
        const data = {
          selectedOpt: null,
          isVZNumber: "",
          imei: "",
          iccid: "",
          selectedCarrier: "",
        };
        setIsCarrier(false);
        setSelectedCarrier("");
        setSelected(null);
        setShowCancel(false);
        handleDeviceUpdate(data, device.id);
      } else {
        if (val !== "Other") {
          const data = {
            selectedOpt: null,
            isVZNumber: "",
            imei: "",
            iccid: "",
            selectedCarrier: selectedCarrier,
          };
          setIsCarrier(true);
          handleDeviceUpdate(data, device.id);
        }
        setInputValue({ imei: "", iccid: "" });
        setIsESIM(false);
        setIsNewSim(false);
        setShowDeviceID(false);
        toggleOptions(false);
        setIsVZNumber("");
      }
      setSelectedCarrier(val);
      featurePreSelect()
    }
    handleDeviceUpdate({ selectedCarrier: val }, device.id);
    if (fromLearn === true) {
      setCarrierSelected(true);
    }
  };

  const handleByodEditClick = (fromEdit = "") => {
    if (!showOptions) {
      toggleOptions();
    }
    setShowForm(true);
    setShowCancel(true);
    setShowProtection(true);
    if (device?.imei) {
      setIsEsimIndicator(true);
      setShowButton(false);
      setimeiCodeValid(true);
      setShowDeviceID(true);
      if (
        selectedCarrier &&
        (selectedCarrier === "Other" || device?.selectedOpt === "esim")
      ) {
        setIsMakeAndModal(true);
      } else {
        setIsMakeAndModal(false);
      }
    } else {
      setIsMakeAndModal(true);
      if (device?.selectedOpt === "esim" && device?.imei) {
        setimeiCodeValid(true);
        setShowDeviceID(true);
        setShowButton(false);
      } else {
        setShowDeviceID(false);
   
        setIsEsimIndicator(false);
      }
    }
    if (!device?.isEditable && device?.completed && fromEdit === "fromEdit") {
      let data = {
        completed: false,
      };
      handleDeviceUpdate(data, device.id);
      let productType = getProductType(device?.deviceInfo?.category ?? "");
      generateOfferText(productType ?? "");
    }
  };

  const handleByodSave = () => {
    dispatch(setReinitiateFlow(true))
    updateDeviceList({});
    setShowForm(false);
  };

  const handleOnDeviceSelect = (selectedOpt, fromLearn = false) => {
    setSelectedDevice(selectedOpt);
    if (!selectedOpt) {
      setCarriersOpts([]);
      setSelectedCarrier("");
      setSelected(null);
      setShowCancel(false);
      setIsNewSim(false);
      setIsOldSim(false);
      setIsESIM(false);
      setIsVZNumber("");
      setIsOldSimVerified(false);
      setInputValue({ imei: "", iccid: "" });
      setShowDeviceID(false);
      return;
    }
    handleDeviceId(device.id);
    setIsMakeAndModal(true);
    fetchSimDetails(selectedOpt);
    setCarriersOpts(selectedOpt.carrier);
    setDeviceInfo({});
    if (isESIM || isNewSim || device.selectedCarrier === "Other") {
      const data = {
        selectedOpt: null,
        isVZNumber: "",
        imei: "",
        iccid: "",
        selectedCarrier: null,
        deviceInfo: selectedOpt,
      };
      setInputValue({ imei: "", iccid: "" });
      setIsESIM(false);
      setSelectedCarrier("");
      setSelected(null);
      setShowDeviceID(false);
      toggleOptions(false);
      handleDeviceUpdate(data, device.id);
      setIsVZNumber("");
    }
    if (fromLearn === true) {
      setDeviceSelected(true);
    }
  };

  const featurePreSelect = () => {
    if (isTMPMDProtectionPlanSelected || isTMPMDProtectionPlanSelectedFromByod // && !isFeaturesRetrieved
    ) {
      if (pageData?.zipCode) {
        setIsProtectionLoading(true);
        dispatch(retrieveFeatureOnBYOD({
          shoppingPath: "PROSPECT",
          flow: "prospect",
          zipCode: pageData?.zipCode,
          getFeatureRequest: getFeatureRequestPayload()
        }));
      }
      setRetriveFeatures(true)
      setShowProtection(true)
    }
  }

  const isVzSelected = (val, fromLearn = false,feature={}) => {
    setIsVZNumber(val);
    if (isPDPProtectionEnabled && !isConnectedDevices) {
      if (pageData?.zipCode && !isFeaturesRetrieved) {
        setIsProtectionLoading(true);
        dispatch(retrieveFeatureOnBYOD({
          shoppingPath: "PROSPECT",
          flow: "prospect",
          zipCode: pageData?.zipCode,
          getFeatureRequest: getFeatureRequestPayload()
        }));
        if(!fromLearn){
          setShowProtection(true);
        }
        updateDeviceList({ isVZNum: val, fromLearn: fromLearn });
        if (fromLearn === true) {
          setShowForm(false);
          setByodDeviceList([]);
          setAddMoreDevice(true);
        }
      } else if (!pageData?.zipCode) {
        if(!fromLearn){
          setShowProtection(true);
        }
        updateDeviceList({ isVZNum: val, fromLearn: fromLearn });
        if (fromLearn === true) {
          setShowForm(false);
          setByodDeviceList([]);
          setAddMoreDevice(true);
        }
      } else {
        if(feature){
          onFeatureSelect(feature, fromLearn);
          updateDeviceList({ isVZNum: val, fromLearn: fromLearn,feature: feature });
        }else{
          onFeatureSelect(selectedFeature, fromLearn);
          updateDeviceList({ isVZNum: val, fromLearn: fromLearn,feature: selectedFeature });
        }
        if (fromLearn === true) {
          setByodDeviceList([]);
          setAddMoreDevice(true);
        }
        const element = document.getElementById(`byod-tile-${sno}`);
        if (element)
          element?.scrollIntoView({ behavior: "smooth", block: "start" });

        if(!feature){
          setShowForm(false);
        }
      }

    } else {
      
        updateDeviceList({ isVZNum: val, fromLearn: fromLearn });
      if (fromLearn === true) {
        setByodDeviceList([]);
        setAddMoreDevice(true);
      }
      const element = document.getElementById(`byod-tile-${sno}`);
      if (element)
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      if(!feature){
        setShowForm(false);
      }
    }
  };
  
  const status = () => {
    let editable = false;
    let cancelable = false;

    editable =
      (deviceInfo &&
        !showForm &&
        (device.selectedOpt === "new" ||
          device.selectedOpt === "old" ||
          device.selectedOpt === "esim")) ||
      (device.isDQ && !inputValue.imei && !showForm) ||
      ((device.isDQ || device.selectedDevice) && !showForm);

    cancelable =
      (showForm && showCancel) || (device.isDQ && showForm && showCancel);
    return {
      editable,
      cancelable,
    };
  };

  const fetchSimDetails = ({ sku, modelName }) => {
    const reqPayload = {
      sku,
      ...payload,
      deviceModel: modelName,
    };
    if (isPDPProtectionEnabled && pageData?.zipCode) {
      reqPayload.zipCode = pageData.zipCode;
      reqPayload.getFeatureRequest = getFeatureRequestPayload({ deviceSku: sku });
    }
    dispatch(getCarrierDetailsByDevice(reqPayload));
  };

  const resetFields = (val = null) => {
    if (showOptions) {
      toggleOptions();
    }
    const data = {
      isVZNumber: "",
      selectedOpt: null,
      imei: "",
      iccid: "",
      deviceInfo: null,
      isOldSimVerified: false,
      completed: false,
      isEditable: false,
      selectedCarrier: null,
      selectedDevice: null,
      displayType: "",
    };
    if (device?.packageId) {
      data.updated = true;
    }
    if (val) {
      data.deviceName = val;
      data.selectedDevice = null;
      data.selectedOpt = null;
    }
    setInputValue({ imei: data?.imei, iccid: data?.iccid });
    handleDeviceUpdate(data, device.id);
    setShowCancel(false);
  };

  const showDeviceIdOption = (val = null) => {
    setShowDeviceID(true);
    setSelectedCarrier(null);
    setIsMakeAndModal(false);
    setIsEsimIndicator(true);
    resetFields(val);
  };

  const showMakeAndModelOption = (val = null) => {
    setShowDeviceID(false);
    setSelectedCarrier(null);
    setIsMakeAndModal(false);
    resetFields(val);
  };

  const inputClickHandler = () => {
    setShowButton(true);
    setIsNewSim(false);
    setIsOldSim(false);
    setIsESIM(false);
    setSelected(null);
    setIsVZNumber("");
    setInputValue({ ...inputValue, iccid: "" });
    setIsOldSimVerified(false);
    setShowForm(true);
    setShowCancel(false);
    if (showOptions) {
      toggleOptions();
    }
  };

  const handleOnSelect = () => {
    setIsNewSim(selected === "new");
    setIsOldSim(selected === "old");
    setIsESIM(selected === "esim");
  };

  const handleActiveEsim = () => {
    setInputValue({ imei: deviceInfo.imei2, iccid: "" });
    setConfirmationModal(false);
    setSelected("old");
    setIsEsimIndicator(false);
    setPsimCapableN(true);
  };

  const handleOnChange = (e) => {
    if (
      inputValue.imei === deviceInfo.imei1 &&
      deviceInfo.euiccCapable === false &&
      e.target.value === "old"
    ) {
      setConfirmationModal(true);
      setSelected(null);
    } else {
      setSelected(e.target.value);
    }
  };

  const handleOnChangeFromLearn = () => {
    if (
      byodDevice?.imei === deviceInfo?.imei1 &&
      deviceInfo?.euiccCapable === false &&
      byodDevice?.selectedOpt === "old"
    ) {
      setConfirmationModal(true);
      setSelected(null);
    } else {
      setSelected(byodDevice?.selectedOpt);
    }
    setSimTypeSelected(true);
  };

  const handleActivePsim = () => {
    setSelected("new");
    setConfirmationModal(false);
  };

  const getFeatureRequestPayload = (currentDevice) => {
    const pkgs = sessionCartData?.cart?.packages;
    const returnList = [];
    // get list of devices from session cart packages
    for (const pkg of Object.values(pkgs || {})) {
      if (!pkg?.dummy && !pkg?.byod) {
        returnList.push({
          deviceSku: pkg.deviceSku,
          simSku: pkg.simSku,
          deviceCategory: pkg?.deviceType === "FOURGROUTER" ? "4g internet" : pkg.deviceCategory
        });
      }
    }
    // get list of previously added BYOD devices
    for (const dev of deviceList) {
      if (dev?.displayType && dev?.id !== device?.id) {
        returnList.push({
          deviceSku: dev.deviceInfo?.deviceSkuId ?? dev?.deviceSku,
          simSku: dev.deviceInfo?.simSku ?? dev?.simSku,
          deviceCategory:
            dev.deviceInfo?.originalCategory ?? dev?.category
        });
      }
    }
    // append current PDP device
    if (currentDevice) {
      returnList.push(currentDevice);
    } else {
      const currDevice = deviceInfo?.deviceSkuId
        ? {
          deviceSku: deviceInfo?.deviceSkuId,
          simSku: deviceInfo?.simSku,
          deviceCategory: deviceInfo?.category,
        }
        : device?.deviceSku
          ? {
            deviceSku: device?.deviceSku,
            simSku: device?.simSku,
            deviceCategory: device?.category,
          }
          : inputValue?.imei
            ? {
              deviceId: inputValue.imei,
            }
            : null;
      if (currDevice) {
        returnList.push(currDevice);
      }
    }
    return returnList;
  };

  const onZipCodeSubmit = (zip) => {
    setIsProtectionLoading(true);
    const payload = {
      shoppingPath: "PROSPECT",
      flow: "prospect",
      zipCode: zip || pageData?.zipCode,
      getFeatureRequest: getFeatureRequestPayload(),
    };
    dispatch(validateZipAndRetrieveFeatureOnBYOD(payload));
  };

  const onFeatureSelect = (feat, fromLearn = false) => {
    setSelectedFeature(feat);
    setShowForm(false);
    if (feat?.displayName?.includes("Total Mobile Protection Multi-Device")) {
      const updatedDevList = [];
      for (const dev of deviceList) {
        if (
          dev?.deviceInfo?.category?.toLowerCase()?.includes("laptop")
        ) {
          // don't add protection to laptop/connected device
          const data = { ...dev };
          updatedDevList.push(data);
        } else if (dev?.completed === true) {
          // update existing devices with TMP-MD
          const data = { ...dev, selectedFeature: feat };
          updatedDevList.push(data);
        } else {
          // add current device
          const data = {
            selectedCarrier: selectedCarrier,
            isVZNumber: (isVZNumber ?? device?.isVZNumber) || "",
            selectedDevice: selectedDevice,
            completed: true,
            displayType: selectedCarrier ? "ByMakeAndMod" : "ByImei",
            isEditable: false,
            selectedOpt: selected,
            imei: inputValue.imei,
            iccid: inputValue.iccid,
            deviceInfo: deviceInfo,
            isOldSimVerified: isOldSimVerified,
            completed: true,
            fromLearn: fromLearn,
            selectedFeature: feat,
          };
          if (device?.packageId) {
            data.updated = true;
          }
          updatedDevList.push(data);
        }
      }
      handleMultiDeviceUpdate(updatedDevList);
    } else {
      updateDeviceList({ feature: feat, fromLearn: fromLearn });
    }
    if (fromLearn === true) {
      setByodDeviceList([]);
      setAddMoreDevice(true);
    }
    const element = document.getElementById(`byod-tile-${sno}`);
    if (element)
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isMultiDeviceProtectPresent = (featureList) => {
    if (featureList?.length) {
      for (const feat of pageData?.PDPFeatureList) {
        if (feat?.name?.includes("Total Mobile Protection Multi-Device")) {
          return true;
        }
      }
    }
    return false;
  };


  return (
    <TileContainer
      aspectRatio="none"
      backgroundColor="#f6f6f6"
      height="100%"
      padding={isMobile ? "16px" : "24px"}
    >
      <div style={{ width: "100%" }} id={`byod-tile-${sno}`}>
        {validateIdLoading && <SpinnerComponent />}
        <ButtonContainer isMobile={isMobile}>
          {status().editable && (
            <span>
              <TextLink
                onClick={() => handleByodEditClick("fromEdit")}
                size={isMobile ? "small" : "medium"}
                type="standAlone"
              >
                {t("byod.byod-collection.edit", { ns: "byod" })}
              </TextLink>
            </span>
          )}
          {status().cancelable && (
            <span>
              <TextLink
                onClick={handleByodSave}
                size={isMobile ? "small" : "medium"}
                type="standAlone"
              >
                {t("byod.byod-collection.save", { ns: "byod" })}
              </TextLink>
            </span>
          )}
          {showRemove && (
            <span>
              <TextLink
                size={isMobile ? "small" : "medium"}
                type="standAlone"
                onClick={handleByodRemoveClick}
              >
                {t("byod.byod-collection.remove", { ns: "byod" })}
              </TextLink>
            </span>
          )}
        </ButtonContainer>
        {showForm  && (
          <Grid
            colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}
            colGutter={false}
          >
            <RowNoPad>
              <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                <Text kind="title" bold size="medium">
                  {t("byod.deviceDescription.deviceSno", {
                    ns: "byod",
                    sno: sno,
                  })}
                </Text>
              </Col>
            </RowNoPad>
            <RowNoPad>
              <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                <MarginSpacerL />
                <Text kind="body" bold size="large">
                  {t("byod.deviceType", { ns: "byod" })}
                </Text>
              </Col>
            </RowNoPad>
            <RowNoPad>
              <Col colSizes={{ mobile: 4, tablet: 8, desktop: 8 }}>
                <MarginSpacerL />
                <DropdownSelect
                  label={t("byod.deviceOptions.label", { ns: "byod" })}
                  onChange={(e) => onDeviceTypeChange(e.target.value)}
                  value={deviceName}
                  disabled={isPlansFirst}
                >
                  {optionsList?.map((option) => {
                    return (
                      <option key={option?.value} value={option?.value}>
                        {option?.label}
                      </option>
                    );
                  })}
                </DropdownSelect>
              </Col>
            </RowNoPad>
          </Grid>
        )}
        {showForm && !isByodAgentLinkPage &&
          (!showDeviceId ||
            device?.selectedOpt === "esim" ||
            device?.selectedCarrier === "Other" ||
            device?.selectedOpt === "new") &&
          device?.deviceName === "smartphone" &&
          (!showDeviceId || device?.selectedOpt === "esim") && (
            <Grid
              colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}
              colGutter={false}
            >
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 8, desktop: 8 }}>
                  <MarginSpacerL />
                  <AsyncWrapper>
                    <AsyncSelectInput
                      defaultValue={device?.selectedDevice?.modelName ?? ""}
                      helperText={t("byod.skipImei.select1PlaceHolder", {
                        ns: "byod",
                      })}
                      label={t("byod.skipImei.select1", { ns: "byod" })}
                      onSelect={handleOnDeviceSelect}
                    />
                  </AsyncWrapper>
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 7, desktop: 7 }}>
                  {device?.selectedOpt === "esim" && (
                    <>
                      <MarginSpacerL />
                      <Text kind="body" size="small">
                        {t("byod.info3", { ns: "byod" })}
                      </Text>
                    </>
                  )}
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                  {device?.selectedOpt !== "esim" &&
                    (!isMakeAndModal || !selectedDevice) && (
                      <>
                        <MarginSpacerS />
                        <TextLink
                          onClick={() => showDeviceIdOption()}
                          size="small"
                          type="standAlone"
                        >
                          {t("byod.noMakeandModel", { ns: "byod" })}
                        </TextLink>
                      </>
                    )}
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 8, desktop: 8 }}>
                  {showForm &&
                    isMakeAndModal &&
                    (device?.selectedOpt !== "esim" || selectedCarrier) &&
                    selectedDevice && (
                      <>
                        <MarginSpacerL />
                        <DropdownSelect
                          error={false}
                          disabled={false}
                          readOnly={false}
                          inlineLabel={false}
                          value={selectedCarrier}
                          helperTextPlacement="bottom"
                          onChange={(e) => handleCarrierSelect(e.target.value)}
                          label={t("byod.skipImei.select2", { ns: "byod" })}
                        >
                          <option>
                            {t("byod.skipImei.select2DefaultOption", {
                              ns: "byod",
                            })}
                          </option>
                          {carriersOpts?.map((carrier) => (
                            <option key={carrier} value={carrier}>
                              {carrier}
                            </option>
                          ))}
                        </DropdownSelect>
                      </>
                    )}
                  {selectedCarrier === "Other" && (
                    <>
                      <MarginSpacerL />
                      <Text kind="body" size="small">
                        {t("byod.info3", { ns: "byod" })}
                      </Text>
                    </>
                  )}
                </Col>
              </RowNoPad>
            </Grid>
          )}
        {showForm && 
          (showDeviceId ||
            (!isByodAgentLinkPage && device?.selectedOpt === "esim") ||
            selectedCarrier === "Other") && (
            <Grid
              colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}
              colGutter={false}
            >
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 8, desktop: 8 }}>
                  <MarginSpacerL />
                  <Input
                    label={t("byod.deviceidLebel", { ns: "byod" })}
                    maxLength={maxLengthValue}
                    placeholder=""
                    name="deviceID"
                    width={"100%"}
                    value={inputValue.imei}
                    onBlur={(e) => {
                      const targetValue = e?.target?.value;
                      if (!targetValue) {
                        setImeiErrorText(
                          t("byod.errorMessages.requiredField"),
                          {
                            ns: "byod",
                          }
                        );
                      }
                    }}
                    success={inputValue.imei.length === 15 && !imeiCodeValid}
                    error={
                      invalidDeviceError ||
                      (!!imeiErrorText && inputValue.imei.length < 15)
                    }
                    errorText={
                      invalidDeviceError ? <div dangerouslySetInnerHTML={{
                          __html: deviceErrorText
                        }}></div> : imeiErrorText
                    }
                    onChange={(e) =>
                      handleFormInputChange(e?.target?.value, "imei")
                    }
                    onClick={(e) => inputClickHandler()}
                  />
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                  {inputValue.imei.length > 0 && !showButton && (
                    <>
                      <MarginSpacerS />
                      <Text kind="body" size="small" color="#000000">
                        {t("byod.deviceinfo-modal.deviceInfo", {
                          ns: "byod",
                        })}
                      </Text>
                      <MarginSpacerS />
                    </>
                  )}
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                  {showForm &&
                    (showDeviceId ||
                      device?.selectedOpt === "esim" ||
                      selectedCarrier === "Other") ? (
                    <>
                      <MarginSpacerS />
                      <TextLink
                        onClick={(e) => handleDeviceDetailsModal(true)}
                        size="small"
                        type="standAlone"
                      >
                        {t("byod.deviceinfo-modal.findDeviceId2", {
                          ns: "byod",
                        })}
                      </TextLink>
                    </>
                  ) : (
                    <></>
                  )}
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                  {device?.selectedOpt !== "esim" && (
                    <>
                      {showButton &&
                        deviceName === "smartphone" &&
                        selectedCarrier !== "Other" && (
                          <>
                            <MarginSpacerS />
                            <TextLink
                              onClick={() => showMakeAndModelOption()}
                              size="small"
                              type="standAlone"
                            >
                              {t("byod.deviceinfo-modal.noDeviceId", {
                                ns: "byod",
                              })}
                            </TextLink>
                          </>
                        )}
                    </>
                  )}
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                  {showButton && (
                    <>
                      <MarginSpacerL />
                      <Button
                        size="small"
                        disabled={
                          (inputValue?.imei != "" && !imeiCodeValid) ||
                          !enableChecker ||
                          invalidDeviceError
                        }
                        use="secondary"
                        onClick={(e) => {
                          const data = {
                            deviceName: deviceName,
                          };
                          handleDeviceUpdate(data, device.id);
                          checkDeviceID()}}
                      >
                        {t("byod.byod-collection.checkDeviceId", {
                          ns: "byod",
                        })}
                      </Button>
                    </>
                  )}
                </Col>
              </RowNoPad>
              <RowNoPad>
                <Col colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}>
                  {showForm && showOptions && (
                    <div className="choice-wrapper">
                      {/* This is esim and psim capable scenario */}
                      {device &&
                        deviceInfo &&
                        deviceInfo?.psimCapable === "Y" &&
                        deviceInfo?.esimCapable === "Y" &&
                        isEsimIndicator &&
                        !psimCapableN && (
                          <>
                            <MarginSpacerL />
                            <Line type="secondary" />
                            <MarginSpacerL />
                            <Text kind="body" size="large" bold color="#000000">
                              {t("byod.byod-collection.haveSim", {
                                ns: "byod",
                              })}
                            </Text>
                            <MarginSpacerL />
                            <SimRadioBoxGroup
                              childHeight={"105px"}
                              data={
                                deviceInfo?.euiccCapable
                                  ? [
                                    {
                                      text: simOptions[2]?.heading ?? " ",
                                      subtext:
                                        simOptions[2]?.description ?? " ",
                                      value: "old",
                                      name: "RadioBoxGroup",
                                    },
                                    {
                                      text: simOptions[0]?.heading ?? " ",
                                      subtext:
                                        simOptions[0]?.description ?? " ",
                                      value: "new",
                                      name: "RadioBoxGroup",
                                    },
                                  ]
                                  : [
                                    {
                                      text: simOptions[0]?.heading ?? " ",
                                      subtext:
                                        simOptions[0]?.description ?? " ",
                                      value: "new",
                                      name: "RadioBoxGroup",
                                    },
                                    {
                                      text: simOptions[2]?.heading ?? " ",
                                      subtext:
                                        simOptions[2]?.description ?? " ",
                                      value: "old",
                                      name: "RadioBoxGroup",
                                    },
                                  ]
                              }
                              defaultValue="none"
                              onChange={(e) => {
                                handleOnChange(e);
                              }}
                              orientation={isMobile ? "vertical" : "horizontal"}
                              surface="light"
                              value={selected}
                            />
                          </>
                        )}
                      {/* This is esim only capable scenario */}
                      {deviceInfo &&
                        deviceInfo?.esimCapable === "Y" &&
                        (deviceInfo?.psimCapable === "N" ||
                          deviceInfo?.psimCapable === undefined ||
                          (psimCapableN &&
                            inputValue?.imei === deviceInfo?.imei2)) && (
                          <>
                            <MarginSpacerL />
                            <Text kind="body" size="small" color="#000000">
                              {t(
                                "byod.byod-collection.completedTile.deviceSIMId",
                                {
                                  ns: "byod",
                                }
                              )}
                            </Text>
                            <MarginSpacerM />
                            <Text kind="body" size="large" color="#000000">
                              {t(
                                "byod.byod-collection.completedTile.embeddedSim",
                                {
                                  ns: "byod",
                                }
                              )}
                            </Text>
                          </>
                        )}
                      {/* This is psim only capable scenario */}
                      {deviceInfo &&
                        deviceInfo?.psimCapable === "Y" &&
                        (deviceInfo?.esimCapable === "N" ||
                          deviceInfo?.psimCapable === undefined) && (
                          <>
                            <MarginSpacerL />
                            <Text kind="body" size="small" color="#000000">
                              {t(
                                "byod.byod-collection.completedTile.deviceSIMId",
                                {
                                  ns: "byod",
                                }
                              )}
                            </Text>
                            <MarginSpacerM />
                            <Text kind="body" size="large" color="#000000">
                              {t("byod.byod-collection.completedTile.newCard", {
                                ns: "byod",
                              })}
                            </Text>
                          </>
                        )}
                    </div>
                  )}
                </Col>
              </RowNoPad>
            </Grid>
          )}
        {showForm &&
          !eSimconfirmationModal &&
          ((selectedCarrier && isMakeAndModal && !isCarrier) || (globalData?.isAgentlink && sessionCartData?.fromAgentLinkPage) ||
            (showOptions &&
              (isNewSim || isESIM || isOldSim) &&
              (selected || !isCarrier || isEsimIndicator))) && (
            <DeviceTileSection
              title={t("byod.skipImei.phoneNumberInfo", { ns: "byod" })}
            >
                {offerTextPortIn && showOfferText ? <Text kind="body" size="large">{offerTextPortIn}</Text> : <></>}
                {offerTextPortIn && showOfferText &&<MarginSpacerS/>}
                <CustomRadioBoxGroup
                childHeight={isMobile ? "84px" : "auto"}
                data={[
                  {
                    text: t("byod.skipImei.existingPhoneNumber", {
                      ns: "byod",
                    }),
                    subtext: t("byod.skipImei.existingPhoneDescription", {
                      ns: "byod",
                    }),
                    value: "Yes",
                    name: "RadioBoxGroup",
                  },
                  {
                    text: t("byod.skipImei.newPhoneNumber", { ns: "byod" }),
                    subtext: t("byod.skipImei.newPhoneDescription", {
                      ns: "byod",
                    }),
                    value: "No",
                    name: "RadioBoxGroup",
                  },
                ]}
                defaultValue="none"
                onChange={(e) => isVzSelected(e.target.value, device?.fromLearn)}
                orientation={isMobile ? "vertical" : "horizontal"}
                surface="light"
                value={isVZNumber}
              />
            </DeviceTileSection>
          )}
        {!!showForm && !!showProtection && !isConnectedDevices && !isTMPMDProtectionPlanSelected && !isTMPMDProtectionPlanSelectedFromByod && (
          <DeviceTileSection
            title={t("byod.byodProtectionContent.title", { ns: "byod" })}
          >
            <ProtectionPlans
              selectedFeature={selectedFeature}
              isLoading={isProtectionLoading}
              isError={isFeatureError}
              onZipCodeSubmit={onZipCodeSubmit}
              onPlanClick={(e) => onFeatureSelect(e, device?.fromLearn)}
              bgColor={ColorTokens.palette.gray95.value}
              isMultiDevAlready={isTMPMDonInit}
              currentLineId={deviceInfo?.deviceSkuId || device?.deviceSku}
              isTMPMDProtectionPlanSelected={isTMPMDProtectionPlanSelected}
              isTMPMDProtectionPlanSelectedFromByod={isTMPMDProtectionPlanSelectedFromByod}
            />
          </DeviceTileSection>
        )}
        {!!showForm && !!showProtection && isVZNumber && !isConnectedDevices && (isTMPMDProtectionPlanSelected || isTMPMDProtectionPlanSelectedFromByod) &&
          <>
            <MarginSpacerL />
            <PDPAccordionSection
              opened={!!showProtectionAcc}
              setOpened={setShowProtectionAcc}
              titleText={t("device-pdp.pdpProtectionContent.title", { ns: "pdp" })}
              subTitleText={selectedFeature?.featureCode
                ? selectedFeature?.displayName
                : t("device-pdp.pdpProtectionContent.subtitle", { ns: "pdp" })}
              dataTrack="ShowProtection"
            >
              <ProtectionPlans
                selectedFeature={selectedFeature}
                isLoading={isProtectionLoading}
                isError={isFeatureError}
                onZipCodeSubmit={onZipCodeSubmit}
                onPlanClick={(e) => onFeatureSelect(e, device?.fromLearn)}
                isMultiDevAlready={isTMPMDonInit}
                currentLineId={deviceInfo?.deviceSkuId || device?.deviceSku}
                isTMPMDProtectionPlanSelected={isTMPMDProtectionPlanSelected}
                isTMPMDProtectionPlanSelectedFromByod={isTMPMDProtectionPlanSelectedFromByod}
              />
            </PDPAccordionSection>
          </>
        }
        {!showForm && (
          <SelectedDevice
            deviceInfo={deviceInfo}
            deviceName={deviceName}
            sno={sno}
            currentDevice={currentDevice}
            selectedDevice={device?.selectedDevice ?? selectedDevice}
            selectedCarrier={selectedCarrier}
            device={device}
            inputValue={inputValue}
            isOldSim={isOldSimVerified}
            isESIM={isESIM}
            isNewSim={isNewSim}
            isVZNumber={isVZNumber}
            selectedFeature={device?.selectedFeature ?? selectedFeature}
          />
        )}
        {eSimconfirmationModal && (
          <EsimConfirmationModal
            showModal={true}
            close={setConfirmationModal}
            handleActiveEsim={handleActiveEsim}
            handleActivePsim={handleActivePsim}
          />
        )}
      </div>
    </TileContainer>
  );
};

export default DeviceTile;
